import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { JotSupportWithPulse } from '@features/jot-support';
import { useIsAuthenticated, useUserState } from '@features/user';
import { FeatureGates } from '@services/statsig/constants';
import { useFeatureGate } from '@services/statsig/hooks';

import { FRONT_CHAT_CONFIG } from '../config';
import { useFrontChatEvents } from '../hooks/useFrontChatEvents';
import { useFrontChatInit } from '../hooks/useFrontChatInit';
import { useFrontChatVisibility } from '../hooks/useFrontChatVisibility';
import {
  closeChatWindow,
  openChatWindow,
  setInitializationStatus,
  setInitialized,
} from '../store/frontChatSlice';
import {
  selectFrontChatLauncherPositionOverride,
  selectFrontChatLauncherVisible,
  selectFrontChatWindowOpen,
} from '../store/selectors';

import { ChatLauncher } from './ChatLauncher';

/** Both support implementations share the existing screen controls. */
export const FrontChat = () => {
  const jotEnabled = useFeatureGate(FeatureGates.JOT_CUSTOMER_SUPPORT);
  const authenticated = useIsAuthenticated();
  const session = useRef(authenticated);
  session.current = authenticated;
  if (!authenticated) return null;
  return jotEnabled ? (
    <JotFrontChat isAuthenticated={() => session.current} />
  ) : (
    <LegacyFrontChat />
  );
};

const useCloseSupportOnUnmount = () => {
  const dispatch = useDispatch();
  useEffect(
    () => () => {
      dispatch(closeChatWindow());
      dispatch(setInitialized(false));
      dispatch(setInitializationStatus('idle'));
    },
    [dispatch],
  );
};

const LegacyFrontChat = () => {
  useCloseSupportOnUnmount();
  const { frontChat, isInitialized } = useFrontChatInit();
  useFrontChatEvents(frontChat, isInitialized);
  useFrontChatVisibility(isInitialized);
  return <ChatLauncher />;
};

const JotFrontChat = ({ isAuthenticated }: { isAuthenticated: () => boolean }) => {
  useCloseSupportOnUnmount();
  const dispatch = useDispatch();
  const authenticated = useIsAuthenticated();
  const user = useUserState({ skip: !authenticated });
  const email = user?.userInfo?.email;
  const identityKey =
    user?.user_id && user.organization_id ? `${user.user_id}:${user.organization_id}` : '';
  const currentIdentity = useRef(identityKey);
  currentIdentity.current = identityKey;
  const ready =
    authenticated &&
    Boolean(user?.user_id) &&
    typeof email === 'string' &&
    Boolean(email) &&
    !FRONT_CHAT_CONFIG.disabledEnvironments.some(
      env => env === String(import.meta.env.VITE_BUILD_ENV),
    );
  const visible = useSelector(selectFrontChatLauncherVisible);
  const open = useSelector(selectFrontChatWindowOpen);
  const position = useSelector(selectFrontChatLauncherPositionOverride);

  useFrontChatVisibility(ready);
  useEffect(() => {
    dispatch(setInitialized(ready));
    dispatch(setInitializationStatus(ready ? 'ready' : 'idle'));
    if (!ready) dispatch(closeChatWindow());
  }, [dispatch, ready]);

  if (!ready || typeof email !== 'string') return null;
  return (
    <JotSupportWithPulse
      key={identityKey || user?.user_id}
      identityKey={identityKey}
      isIdentityCurrent={() => isAuthenticated() && currentIdentity.current === identityKey}
      replyEmail={email}
      launcherVisible={visible}
      open={open}
      onOpenChange={value => dispatch(value ? openChatWindow() : closeChatWindow())}
      launcherPosition={position}
    />
  );
};
